/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { formatTimestamp, timeAgo } from "../../utils/format-timestamp"
import type { ValueOf } from "../../utils/types"
import { valueOrDash } from "../../utils/value-or-dash"

import styles from "./timestamp.module.css"

/**
 * How a timestamp reads at rest: `2 days ago`, or `24 Sep 2026, 14:32`.
 */
export const Format = {
    Absolute: "absolute",
    Relative: "relative"
} as const
export type Format = ValueOf<typeof Format>

interface Props {
    /**
     * ISO timestamp from the API, absent when the event never happened.
     *
     * A missing value reads as an em dash in both formats.
     */
    timestamp: string | undefined
    /**
     * Shown at rest; the other format replaces it on hover.
     *
     * @default Format.Relative
     */
    format?: Format
}

/**
 * A timestamp that shows one format at rest and the other on hover, so a reader
 * can get the exact time without leaving the row.
 *
 * Both formats sit in the same grid cell, so the swap does not move anything
 * around it. A wrapper can widen the hover area past the text itself by marking
 * itself `data-timestamp="hover-area"`, as `TimestampWithIcon` does.
 *
 * @example
 * <Timestamp timestamp={event.timestampCreated} />
 * <Timestamp format={Format.Absolute} timestamp={device.timestampLastSeen} />
 */
export const Timestamp = ({ format = Format.Relative, timestamp }: Props) => {
    const absolute = formatTimestamp(timestamp)
    const isRelative = format === Format.Relative
    const [primary, secondary] = isRelative ? [timeAgo(timestamp), absolute] : [absolute, timeAgo(timestamp)]

    return (
        // The absolute value truncates under a narrow container, so it is repeated
        // as a tooltip to stay readable.
        <span className={styles.timestamp} title={absolute || undefined}>
            <span className={styles.primary}>{valueOrDash(primary)}</span>
            <span className={styles.secondary}>{valueOrDash(secondary)}</span>
        </span>
    )
}
